// apps/web/src/components/ModalOverlay.tsx
import { createPortal } from 'react-dom';
import type { HTMLAttributes } from 'react';

/**
 * Backdrop for every centered dialog: the shared `.modal-overlay` class plus
 * whatever the dialog passes (its own bg-*, z-index, role/aria attributes).
 *
 * Rendered into `document.body` via a portal. `.modal-overlay` is
 * `position: fixed`, which only resolves against the viewport when no ancestor
 * establishes a containing block — but the page containers use Tailwind's
 * `animate-fade-up`, whose `animation-fill-mode: both` leaves a permanent
 * `transform` behind. A dialog rendered inside one of those would size and
 * position itself against the page instead of the screen, which is exactly the
 * "opens already scrolled halfway down" bug. Portalling sidesteps it for every
 * dialog at once. React events still bubble through the React tree, so dialogs
 * keep whatever handlers their parents had.
 */
export function ModalOverlay({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return createPortal(
    <div className={className ? `modal-overlay ${className}` : 'modal-overlay'} {...rest}>
      {children}
    </div>,
    document.body,
  );
}
